import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { PROVINSI } from "@/utils/indonesiaData";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const MODES = [
  { id: "ibukota", label: "Tebak Ibu Kota" },
  { id: "provinsi", label: "Tebak Provinsi" },
];

const TebakProvinsi = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [mode, setMode] = useState("ibukota");
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const newQuestion = useCallback(() => {
    const correct = PROVINSI[Math.floor(Math.random() * PROVINSI.length)];
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = PROVINSI[Math.floor(Math.random() * PROVINSI.length)];
      if (w.name !== correct.name && !wrongs.find(x => x.name === w.name)) wrongs.push(w);
    }
    const all = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setTarget(correct); setOpts(all); setFeedback(null);
    setTimeout(() => {
      if (soundOn) {
        if (mode === "ibukota") speakText(`Apa ibu kota provinsi ${correct.name}?`);
        else speakText(`Provinsi mana yang ibu kotanya ${correct.capital}?`);
      }
    }, 300);
  }, [mode, soundOn]);

  useEffect(() => { setScore(0); setRound(0); setWon(false); newQuestion(); }, [mode, newQuestion]);

  const handlePick = (a) => {
    if (feedback) return;
    const isCorrect = a.name === target.name;
    if (isCorrect) {
      setFeedback({ type: "ok", text: `Benar! ${mode === "ibukota" ? target.capital : target.name}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! ${mode === "ibukota" ? target.capital : target.name}!`);
    } else {
      setFeedback({ type: "wrong", text: `Yang benar: ${mode === "ibukota" ? target.capital : target.name}` });
      if (soundOn) speakText(`The correct answer is ${mode === "ibukota" ? target.capital : target.name}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) {
        setWon(true);
        recordGameWon("provinsi");
      } else { setRound(r => r + 1); newQuestion(); }
    }, 1500);
  };

  if (won) {
    return (
      <div className="quiz-container flex flex-col items-center justify-center min-h-[80vh]" data-testid="provinsi-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center w-full">
          <span className="text-6xl block mb-3">🇮🇩</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); newQuestion(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setMode(mode === "ibukota" ? "provinsi" : "ibukota")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Ganti ke {mode === "ibukota" ? "Tebak Provinsi" : "Tebak Ibu Kota"}</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="quiz-container flex flex-col items-center pt-3" data-testid="provinsi-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🇮🇩 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="flex gap-2 mb-3 w-full">
        {MODES.map(m => (
          <Button key={m.id} data-testid={`provinsi-mode-${m.id}`} onClick={() => setMode(m.id)} className={`flex-1 font-fredoka font-bold rounded-xl h-10 ${mode === m.id ? "bg-gold text-brown-dark" : "glass-card text-gold"}`}>{m.label}</Button>
        ))}
      </div>

      <motion.div key={`${mode}-${round}`} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <span className="block mb-2" style={{ fontSize: "clamp(3.5rem, 9vw, 6rem)" }}>{target?.emoji}</span>
        <p className="font-fredoka text-gold-light/70 text-sm mb-1">{mode === "ibukota" ? "Ibu kota dari:" : "Provinsi dari:"}</p>
        <p className="font-fredoka text-gold font-bold quiz-question-text">{mode === "ibukota" ? target?.name : target?.capital}</p>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`provinsi-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full">
        {opts.map((a, i) => (
          <motion.button key={`${a.name}-${i}`} data-testid={`provinsi-opt-${i}`}
            onClick={() => handlePick(a)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn text-center text-slate-800 font-fredoka font-bold">
            {mode === "ibukota" ? a.capital : a.name}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TebakProvinsi;
